/**
 * useStornoEditing
 * Чистые функции для режима корректировки/сторно:
 *   — расчёт максимально допустимого нового факта
 *   — применение ввода нового факта и материалов
 *   — diff материалов и сборка JSON-троек для uspFactCorrectionSave
 *   — фабрика дефолтных сторно-полей для state-объектов (ячейка / item)
 *
 * Сознательно без реактивных хуков Vue, чтобы легко вызывать и из
 * Options API (через методы-обёртки), и из Composition API, и из
 * обычных функций (submit-методы в index.vue).
 *
 * Все зависимости (parseFactNum, r6, notify) пробрасываются явно —
 * чтобы тесты и вызовы из разных компонентов не тащили this.
 */

function findOriginalMaterial(originalMaterials, materialId) {
  return (originalMaterials || []).find((m) => m.material_id === materialId) || null;
}

export function computeMaxFactValue({ plan, cumulative, exceedAllowed, r6 }) {
  if (exceedAllowed) return null;
  const p = Number(plan) || 0;
  const c = Number(cumulative) || 0;
  if (p <= 0) return null;
  return Math.max(0, r6(p - c));
}

export function applyNewFactInput({ val, originalValue, maxVal, parseFactNum, r6, notify }) {
  let num = parseFactNum(val);
  if (Number.isNaN(num) || num < 0) num = 0;
  if (maxVal !== null && maxVal !== undefined && num > maxVal) {
    num = maxVal;
    notify?.({ type: 'warning', message: 'Новый факт ограничен остатком по работе' });
  }
  const orig = parseFactNum(originalValue);
  return {
    newFactValue: String(r6(num)),
    stornoDiff:   r6(num - orig),
  };
}

export function getOriginalMaterialValue(originalMaterials, mat) {
  const om = findOriginalMaterial(originalMaterials, mat.material_id);
  return om?.originalValue ?? '0';
}

export function getMaterialNewValue(originalMaterials, mat) {
  const om = findOriginalMaterial(originalMaterials, mat.material_id);
  return om?.newValue ?? '0';
}

export function getMaterialDiff({ originalMaterials, mat, parseFactNum, r6 }) {
  const om = findOriginalMaterial(originalMaterials, mat.material_id);
  if (!om) return 0;
  return r6(parseFactNum(om.newValue) - parseFactNum(om.originalValue));
}

export function applyMaterialInput({ val, mat, originalMaterials, parseFactNum, r6, notify }) {
  let num = parseFactNum(val);
  if (Number.isNaN(num) || num < 0) num = 0;
  if (mat?.remainder != null && mat.remainder !== '') {
    const rem  = parseFactNum(mat.remainder);
    const orig = parseFactNum(findOriginalMaterial(originalMaterials, mat.material_id)?.originalValue);
    const maxVal = Math.max(0, r6(rem + orig));
    if (num > maxVal) {
      num = maxVal;
      notify?.({ type: 'warning', message: `Материал "${mat.name}" ограничен остатком по ЖЦП` });
    }
  }
  const om = findOriginalMaterial(originalMaterials, mat.material_id);
  if (om) {
    om.newValue = String(r6(num));
    om.isManual = true;
  }
}

/**
 * Пересчёт новых значений материалов по норме расхода при изменении newFactValue.
 * Логика совпадает с обычным режимом (_recalcMaterials в PpmFactDialog):
 *   — если для материала есть plan_amount и workPlan > 0 → плановая доля * (factNum / workPlan);
 *   — иначе → factNum * consumption_rate.
 * Материалы с isManual=true не трогаются.
 * Уважает остаток по ЖЦП (если задан mat.remainder): итог = min(auto, origValue + remainder).
 */
export function recalcStornoMaterialsByFact({
  newFactValue,
  originalMaterials,
  workMaterials,
  workPlan,
  parseFactNum,
  r6,
}) {
  const factNum = parseFactNum(newFactValue);
  const plan    = parseFactNum(workPlan);
  const mats    = originalMaterials || [];
  const wmats   = workMaterials || [];
  mats.forEach((om) => {
    if (om.isManual) return;
    const wm = wmats.find((m) => m.material_id === om.material_id);
    if (!wm) return;
    const planAmount = parseFactNum(wm.plan_amount);
    const norm       = parseFactNum(wm.norm ?? wm.consumption_rate);
    let auto = plan > 0 && planAmount > 0
      ? r6(planAmount * (factNum / plan))
      : r6(factNum * norm);
    if (auto < 0) auto = 0;
    if (wm.remainder != null && wm.remainder !== '') {
      const rem  = parseFactNum(wm.remainder);
      const orig = parseFactNum(om.originalValue);
      const cap  = Math.max(0, r6(rem + orig));
      if (auto > cap) auto = cap;
    }
    om.newValue = String(r6(auto));
  });
}

export function buildMaterialsJsonTriplet({ originalMaterials, workMaterials, parseFactNum, r6 }) {
  const mats = originalMaterials || [];
  const work = workMaterials || [];
  const nameOf = (id) => work.find((m) => m.material_id === id)?.name ?? '';

  const originalMaterialsJson = mats.map((om) => ({
    material_id:    om.material_id,
    material_value: parseFactNum(om.originalValue),
    material_name:  nameOf(om.material_id),
  }));
  const newMaterialsJson = mats.map((om) => ({
    material_id:    om.material_id,
    material_value: parseFactNum(om.newValue),
    material_name:  nameOf(om.material_id),
  }));
  const diffMaterialsJson = mats.map((om) => ({
    material_id:    om.material_id,
    material_value: r6(parseFactNum(om.newValue) - parseFactNum(om.originalValue)),
    material_name:  nameOf(om.material_id),
  }));
  return { originalMaterialsJson, newMaterialsJson, diffMaterialsJson };
}

export function hasMaterialsDiff(diffMaterialsJson) {
  return (diffMaterialsJson || []).some((m) => m.material_value !== 0);
}

export function createStornoFields(factValue = 0) {
  const s = factValue === null || factValue === undefined || factValue === '' ? '0' : String(factValue);
  return {
    originalFactValue: s,
    newFactValue:      s,
    stornoDiff:        0,
    originalMaterials: [],
  };
}

export function buildOriginalMaterialsFromWork(materials) {
  return (materials || []).map((m) => ({
    material_id:   m.material_id,
    originalValue: String(m.value ?? 0),
    newValue:      String(m.value ?? 0),
    isManual:      false,
  }));
}
